import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getDynamicLogoPath } from "../../utils/functions";
import { useTranslation } from "react-i18next";

const Last10GamesChart = ({
  competitionId,
  homeTeam,
  awayTeam,
  standings,
  teamColors,
  week,
  totalStandingTeams,
  strokeWidth = 25,
  isOf = false
}) => {
  const [animationStep, setAnimationStep] = useState(0);
  const totalSteps = standings.length * 2;
  const homeTeamId = homeTeam.id;
  const awayTeamId = awayTeam.id;
  const totalTeams = totalStandingTeams || 18;
  var weekNumber = parseInt(week, 10) || 26;
  const winner = Object.entries(
    standings.reduce((acc, obj) => {
      for (const key in obj) acc[key] = (acc[key] || 0) + obj[key];
      return acc;
    }, {}),
  ).reduce((min, curr) => (curr[1] < min[1] ? curr : min))[0];

  const yTicks = Array.from({ length: totalTeams }, (_, i) => i + 1);

  // Filter standings to exclude teams that haven't played
  const validTeams = standings.reduce((acc, match) => {
    Object.keys(match).forEach((teamId) => {
      if (match[teamId] !== null && match[teamId] !== undefined) {
        acc.add(teamId);
      }
    });
    return acc;
  }, new Set());

  // Check if home or away team is in valid standings
  const isHomeTeamValid = validTeams.has(homeTeamId);
  const isAwayTeamValid = validTeams.has(awayTeamId);

  // Prepare data, only for valid teams
  let filteredStandings = [];
  if (weekNumber < 10) {
    const startIndex = standings?.length - weekNumber;
    filteredStandings = Array.from({ length: 10 }, (_, index) => {
      if (index >= weekNumber) {
        return { matchday: index + 1 }; // Empty points for remaining matchdays
      }

      const actualIndex = index + startIndex;
      const match = standings[actualIndex];

      return {
        matchday: index + 1, // Maps data to matchdays 1 to maxWeeks
        ...(isHomeTeamValid && { [homeTeamId]: match?.[homeTeamId] - 0.5 }),
        ...(isAwayTeamValid && { [awayTeamId]: match?.[awayTeamId] - 0.5 }),
      };
    });
  } else {
    filteredStandings = standings.map((match, index) => ({
      matchday: index + 1,
      ...(isHomeTeamValid && { [homeTeamId]: match[homeTeamId] - 0.5 }),
      ...(isAwayTeamValid && { [awayTeamId]: match[awayTeamId] - 0.5 }),
    }));
  }

  const generateXAxisLabels = (weekNumber) => {
    if (isNaN(weekNumber)) {
      console.error("Invalid week value:", week);
      return [];
    }

    if (weekNumber < 10) {
      return Array.from({ length: 10 }, (_, i) => i + 1);
    }

    return Array.from({ length: 10 }, (_, i) => weekNumber - 9 + i);
  };

  const xAxisLabels = generateXAxisLabels(weekNumber);

  const CustomDot = ({
    cx,
    cy,
    index,
    fill,
    stroke,
    team,
    competitionId,
    animationStep,
    data,
  }) => {
    let isLastPoint = false;
    if (!data || data.length === 0) return null;
    const hasValidData = (match) =>
      Object.keys(match).some((key) => key !== "matchday");

    if (weekNumber < 10) {
      const lastValidIndex = data
        .map((match, idx) => (hasValidData(match) ? idx : -1))
        .filter((idx) => idx !== -1)
        .pop();

      if (!hasValidData(data[index])) return null;
      isLastPoint = index === lastValidIndex;
    } else {
      isLastPoint = index === standings.length - 1;
    }

    const src = getDynamicLogoPath({ team, competitionId });

    if (index > Math.floor(animationStep / 2)) return null;

    const dotAnimationClass = animationStep > index * 2 ? "dot-appear" : "";

    const handleError = (event) => {
      const currentTarget = event.currentTarget;
      if (!currentTarget || !currentTarget.href || !currentTarget.href.baseVal)
        return;

      const originalSrc = currentTarget.href.baseVal;

      if (
        (originalSrc.includes("soccer/team") ||
          originalSrc.includes("soccer/competition")) &&
        originalSrc.endsWith(".png")
      ) {
        currentTarget.href.baseVal = originalSrc.replace(".png", ".svg");
      } else if (!originalSrc.includes("?fallback=true")) {
        currentTarget.href.baseVal += "?fallback=true";
      }
    };

    return (
      <g>
        {!isLastPoint ? (
          // Render the default circle for all points except the last one
          <circle
            cx={cx}
            cy={cy}
            r={8}
            fill={fill}
            stroke={stroke}
            strokeWidth={3}
            className={dotAnimationClass}
          />
        ) : (
          // Render image for the last point
          <image
            href={src}
            x={cx - 30}
            y={cy - 30}
            width="60"
            height="60"
            onError={handleError}
          />
        )}
      </g>
    );
  };

  const getLinePathLength = () => {
    const visibleSegments = Math.floor(animationStep / 2);
    const segmentLength = 1000 / (standings.length - 1);

    if (animationStep % 2 === 1 && visibleSegments < standings.length) {
      return visibleSegments * segmentLength;
    }
    return visibleSegments * segmentLength;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep((prev) => {
        if (prev < totalSteps) {
          return prev + 1;
        } else {
          clearInterval(interval);
          return prev;
        }
      });
    }, 100);

    return () => clearInterval(interval);
  }, [totalSteps]);

  const { t } = useTranslation();

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <ResponsiveContainer width={"100%"} height={700}>
        <LineChart
          data={filteredStandings}
          margin={{ top: 50, right: 30, left: 10, bottom: 20 }}
        >
          <defs>
            <linearGradient id="gridGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#0d3e4f" />
              <stop offset="100%" stopColor="#0d283d" />
            </linearGradient>
          </defs>
          <CartesianGrid
            vertical={false}
            strokeLinecap="round"
            stroke="#ccc"
            strokeOpacity={0.5}
            fill={isOf ? null : "url(#gridGradient)"}
          />

          <XAxis
            stroke="#fff"
            orientation="top"
            dataKey="week"
            tickFormatter={(value, index) => xAxisLabels[index]}
            tick={{ fill: "#fff", fontSize: 16 }}
            padding={{ left: 40, right: 40 }}
            label={{
              value: t("Matchday"),
              position: "top",
              offset: 20,
              fill: "#fff",
              fontSize: 35,
              letterSpacing: 1.5
            }}
          />
          <YAxis
            stroke="#fff"
            domain={[0, totalTeams]}
            ticks={yTicks}
            tick={({ payload, x }) => {
              const tickSpacing =
                yTicks.length > 1
                  ? Math.abs(yTicks[1] - yTicks[0])
                  : totalTeams;
              const halfSpacing = tickSpacing / 2;

              return (
                <g transform={`translate(${x},${payload.coordinate})`}>
                  <rect x={-32} y={-32} width={49} height={34} fill="none" />
                  <line
                    x1={-32}
                    x2={isOf ? 15.5 : 18}
                    y1={isOf ? 0 : halfSpacing}
                    y2={isOf ? 0 : halfSpacing}
                    stroke={isOf ? '#ccc' : "#587580"}
                    strokeWidth={isOf ? 0.5 : 2}
                  />
                  <text
                    x={-5}
                    y={1}
                    dy={-12}
                    fill="#fff"
                    fontSize={16}
                    textAnchor="middle"
                  >
                    {payload.value + "."}
                  </text>
                </g>
              );
            }}
            axisLine={{ stroke: isOf ? 'none' : "#1c3042", strokeWidth: 2 }}
            tickLine={false}
            reversed={true}
            tickMargin={10}
            label={({ viewBox }) => {
              const { x, y } = viewBox;
              return (
                <g transform={`translate(${x + 30}, ${y - 20})`}>
                  <text
                    x={0}
                    y={0}
                    fill="#fff"
                    fontSize={22}
                    textAnchor="middle"
                  >
                    Pos.
                  </text>
                  {/* Sticky Line Under "Pos" */}
                  <line
                    x1={-18}
                    x2={30}
                    y1={20}
                    y2={20}
                    stroke="#96aab1"
                    strokeWidth={2}
                  />
                </g>
              );
            }}
          />

          {/* <Tooltip /> */}

          {/* Lines with circular points */}
          {isHomeTeamValid && (
            <>
              {/* Home team shadow line for glow */}
              <Line
                type="monotone"
                dataKey={homeTeam.id}
                stroke="white"
                strokeWidth={strokeWidth}
                strokeOpacity={0.3}
                dot={false}
                isAnimationActive={true}
                strokeDasharray={`${getLinePathLength()} ${1000 - getLinePathLength()}`}
                strokeLinejoin="round"
                strokeLinecap="round"
              />

              <Line
                type="monotone"
                dataKey={homeTeam.id}
                stroke={teamColors?.home}
                strokeWidth={6}
                dot={(props) => (
                  <CustomDot
                    {...props}
                    fill={
                      winner === homeTeam.id ? `${teamColors?.home}` : "#597884"
                    }
                    stroke={
                      winner === homeTeam.id
                        ? "transparent"
                        : `${teamColors?.home}`
                    }
                    team={homeTeam}
                    competitionId={competitionId}
                    animationStep={animationStep}
                    data={filteredStandings}
                  />
                )}
                activeDot={{
                  r: 8,
                  fill:
                    winner === homeTeam.id ? `${teamColors?.home}` : "#597884",
                  stroke: `${teamColors?.home}`,
                }}
                isAnimationActive={false}
                strokeDasharray={`${getLinePathLength()} ${1000 - getLinePathLength()}`}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </>
          )}

          {/* Away team shadow line for glow */}
          {isAwayTeamValid && (
            <>
              <Line
                type="monotone"
                dataKey={awayTeam.id}
                stroke="white"
                strokeWidth={strokeWidth}
                strokeOpacity={0.3}
                dot={false}
                isAnimationActive={true}
                strokeDasharray={`${getLinePathLength()} ${1000 - getLinePathLength()}`}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <Line
                type="monotone"
                dataKey={awayTeam.id}
                stroke={teamColors?.away}
                strokeWidth={6}
                dot={(props) => (
                  <CustomDot
                    {...props}
                    fill={
                      winner === awayTeam.id ? `${teamColors?.away}` : "#597884"
                    }
                    stroke={
                      winner === awayTeam.id
                        ? "transparent"
                        : `${teamColors?.away}`
                    }
                    team={awayTeam}
                    competitionId={competitionId}
                    animationStep={animationStep}
                    data={filteredStandings}
                  />
                )}
                activeDot={{
                  r: 8,
                  fill:
                    winner === awayTeam.id ? `${teamColors?.away}` : "#597884",
                  stroke: `${teamColors?.away}`,
                }}
                isAnimationActive={false}
                strokeDasharray={`${getLinePathLength()} ${1000 - getLinePathLength()}`}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Last10GamesChart;
